import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { useAuth } from "../../auth/store";
import { useCan } from "../../auth/roles";
import { EmptyPlugMark, EmptyState } from "../../components/States";
import { SectionTabs } from "../../components/ui";
import { BACKEND_RANGES, backendApi, backendKeys, type BackendRange, type MetricAgg } from "./api";

export function BackendTabs() {
  return (
    <SectionTabs label="Backend">
      <Link to={"/backend" as never} activeOptions={{ exact: true, includeSearch: false }}>Traces</Link>
      <Link to={"/backend/logs" as never} activeOptions={{ includeSearch: false }}>Logs</Link>
      <Link to={"/backend/metrics" as never} activeOptions={{ includeSearch: false }}>Metrics</Link>
      <Link to={"/backend/services" as never} activeOptions={{ includeSearch: false }}>Services</Link>
      <Link to={"/backend/map" as never} activeOptions={{ includeSearch: false }}>Map</Link>
    </SectionTabs>
  );
}

/** Header actions on every Backend tab: the time window, setup, and settings for admins. */
export function BackendActions({ range = true }: { range?: boolean }) {
  const can = useCan();
  return (
    <>
      {range && <BackendRangePicker />}
      <Link to={"/backend/connect" as never} className="btn">Connect a service</Link>
      {can.admin && <Link to={"/backend/settings" as never} className="btn">Settings</Link>}
    </>
  );
}

/** Back to the Backend tabs, for the setup and settings pages. */
export function BackToBackend() {
  return (
    <Link to={"/backend" as never} className="btn ghost icon-btn" aria-label="Back to Backend" title="Back to Backend">
      ←
    </Link>
  );
}

const RANGE_IDS = new Set<string>(BACKEND_RANGES.map((r) => r.id));

/** The time window shared by every Backend tab, kept in `?range=`. */
export function useBackendRange(): [BackendRange, (r: BackendRange) => void] {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const range = (typeof search.range === "string" && RANGE_IDS.has(search.range) ? search.range : "24h") as BackendRange;
  const set = (r: BackendRange) => void navigate({ to: pathname as never, search: { ...search, range: r === "24h" ? undefined : r } as never, replace: true });
  return [range, set];
}

export function BackendRangePicker() {
  const [range, setRange] = useBackendRange();
  return (
    <select className="input be-range" aria-label="Time range" value={range} onChange={(e) => setRange(e.target.value as BackendRange)} autoComplete="off">
      {BACKEND_RANGES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
    </select>
  );
}

/** URL-backed filters for one Backend tab. Empty values are dropped from the URL. */
export function useFilters<K extends string>(keys: readonly K[]): [Partial<Record<K, string>>, (patch: Partial<Record<K, string | undefined>>) => void, () => void] {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const filters = useMemo(() => {
    const f: Partial<Record<K, string>> = {};
    for (const k of keys) if (typeof search[k] === "string" && search[k] !== "") f[k] = search[k] as string;
    return f;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
  const set = (patch: Partial<Record<K, string | undefined>>) => {
    const next: Record<string, unknown> = { ...search, ...patch };
    for (const k of Object.keys(next)) if (next[k] === undefined || next[k] === "") delete next[k];
    void navigate({ to: pathname as never, search: next as never, replace: true });
  };
  const clear = () => void navigate({ to: pathname as never, search: (search.range ? { range: search.range } : {}) as never, replace: true });
  return [filters, set, clear];
}

/** A search box that writes to the URL a moment after typing stops. */
export function DebouncedSearch({ value, onChange, placeholder, label }: { value?: string; onChange: (v: string | undefined) => void; placeholder: string; label: string }) {
  const [text, setText] = useState(value ?? "");
  useEffect(() => setText(value ?? ""), [value]);
  useEffect(() => {
    const t = window.setTimeout(() => {
      const v = text.trim() || undefined;
      if (v !== value) onChange(v);
    }, 250);
    return () => window.clearTimeout(t);
    // onChange follows the current URL filters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, value]);
  return (
    <label className="issues-search">
      Search
      <input placeholder={placeholder} value={text} onChange={(e) => setText(e.target.value)} aria-label={label} autoComplete="off" />
    </label>
  );
}

export function useFacets(range: BackendRange) {
  const auth = useAuth();
  return useQuery({
    queryKey: backendKeys.facets(auth.currentProjectId, range),
    queryFn: () => backendApi.facets(range),
    enabled: !!auth.currentProjectId,
    staleTime: 60_000,
  });
}

export function ServiceSelect({ value, onChange, services }: { value?: string; onChange: (v: string | undefined) => void; services: string[] }) {
  return (
    <label>
      Service
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value || undefined)} autoComplete="off">
        <option value="">All services</option>
        {services.map((s) => <option key={s} value={s}>{s}</option>)}
        {value && !services.includes(value) && <option value={value}>{value}</option>}
      </select>
    </label>
  );
}

export function levelName(n: number): "fatal" | "error" | "warn" | "info" | "debug" {
  if (n >= 21) return "fatal";
  if (n >= 17) return "error";
  if (n >= 13) return "warn";
  if (n >= 9) return "info";
  return "debug";
}

const LEVEL_BADGE = { fatal: "danger", error: "danger", warn: "warn", info: "info", debug: "neutral" } as const;
const LEVEL_LABEL = { fatal: "Fatal", error: "Error", warn: "Warning", info: "Info", debug: "Debug" } as const;

export function LevelBadge({ severity }: { severity: number }) {
  const l = levelName(severity);
  return <span className={`badge ${LEVEL_BADGE[l]}`}>{LEVEL_LABEL[l]}</span>;
}

export function StatusBadge({ status, httpStatus }: { status: string; httpStatus?: number }) {
  if (status === "error") return <span className="badge danger">{httpStatus ? httpStatus : "Error"}</span>;
  if (httpStatus && httpStatus >= 400) return <span className="badge warn">{httpStatus}</span>;
  return <span className="badge ok">{httpStatus || "OK"}</span>;
}

export function ms(v: number): string {
  if (!Number.isFinite(v)) return "—";
  if (v < 1) return `${v.toFixed(2)} ms`;
  if (v < 1000) return `${Math.round(v)} ms`;
  if (v < 60_000) return `${(v / 1000).toFixed(v < 10_000 ? 2 : 1)} s`;
  return `${(v / 60_000).toFixed(1)} min`;
}

export const AGG_LABEL: Record<MetricAgg, string> = {
  sum: "Total",
  rate: "Per second",
  avg: "Average",
  min: "Lowest",
  max: "Highest",
  last: "Latest",
  count: "Count",
  p50: "Median",
  p95: "95th percentile",
  p99: "99th percentile",
};

const UNIT_NAMES: Record<string, string> = {
  By: "bytes", KiBy: "KiB", MiBy: "MiB", GiBy: "GiB",
  s: "seconds", ms: "ms", us: "µs", ns: "ns", min: "minutes", h: "hours",
  "%": "%", "1": "",
};

/** Metric units arrive as standard unit codes ("By", "s", "{request}"); people read words. */
export function unitName(u?: string): string {
  if (!u) return "";
  const named = UNIT_NAMES[u];
  if (named !== undefined) return named;
  return /^\{(.+)\}$/.exec(u)?.[1] ?? u;
}

export function aggsFor(kind: string): MetricAgg[] {
  if (kind === "counter") return ["rate", "sum"];
  if (kind === "histogram") return ["p95", "p50", "p99", "avg", "max", "count"];
  return ["avg", "last", "min", "max"];
}

export function when(epochMs: number): string {
  return new Date(epochMs).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

export function pct(part: number, whole: number): string {
  if (!whole || !part) return "0%";
  const v = (part / whole) * 100;
  if (v < 0.01) return "<0.01%";
  return `${v < 1 ? v.toFixed(2) : v.toFixed(1)}%`;
}

/** Where a request with a replay should open: the replay, at the moment the request started. */
export function replayLink(sessionId: string, atMs: number) {
  return { to: "/sessions/$id" as const, params: { id: sessionId }, search: { at: atMs } };
}

/** Shown on a tab with nothing to show yet, pointing at the setup guide. */
export function ConnectPrompt({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="card section">
      <EmptyState
        mark={<EmptyPlugMark />}
        title={title}
        hint={hint}
        action={<Link to={"/backend/connect" as never} className="btn primary">Connect your backend</Link>}
      />
    </div>
  );
}
